import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MarkdownComponent } from '../../markdown/markdown.component';
import { MessageFileChipComponent } from '../../message-file-chip/message-file-chip.component';
import { ToolArgumentsComponent } from '../../tool-arguments/tool-arguments.component';
import { MessagesListItemProps } from './messages-list-item.interface';

function formatToolPayload(value: string) {
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
}

const thinkingShimmerSx = {
  backgroundImage:
    'linear-gradient(90deg, #9e9e9e 0%, #f5f5f5 45%, #9e9e9e 90%)',
  backgroundSize: '200% 100%',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  color: 'transparent',
  animation: 'thinking-shimmer 1.6s linear infinite',
  '@keyframes thinking-shimmer': {
    '0%': { backgroundPosition: '100% 0' },
    '100%': { backgroundPosition: '-100% 0' },
  },
};

function MessagesListItemThinking({
  thinking,
  isThinking,
}: {
  thinking: string;
  isThinking: boolean;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  return (
    <Accordion
      disableGutters
      expanded={expanded}
      onChange={(_, nextExpanded) => {
        setExpanded(nextExpanded);
      }}
      sx={{
        width: '100%',
        boxShadow: 'none',
        bgcolor: 'transparent',
        backgroundImage: 'none',
        '&::before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon fontSize="small" />}
        sx={{
          minHeight: 36,
          px: 1.5,
          '& .MuiAccordionSummary-content': {
            my: 0.75,
          },
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={isThinking ? thinkingShimmerSx : undefined}
        >
          {t('messages.thinking')}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
        {thinking ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            {thinking}
          </Typography>
        ) : null}
      </AccordionDetails>
    </Accordion>
  );
}

export function MessagesListItemComponent({
  message,
  isStreaming = false,
  downloadFile,
  onToolApproval,
  isToolApprovalLoading = false,
}: MessagesListItemProps) {
  const { t } = useTranslation();
  const toolName = useMemo(
    () => message.toolCalls?.function.name ?? t('messages.toolCall'),
    [message.toolCalls, t],
  );
  const toolArguments = useMemo(
    () => message.toolCalls?.function.arguments ?? '',
    [message.toolCalls],
  );
  const thinking = useMemo(() => message.thinking ?? '', [message.thinking]);
  const isThinking = useMemo(
    () => isStreaming && !message.text,
    [isStreaming, message.text],
  );
  const files = useMemo(() => message.files ?? [], [message.files]);

  if (message.role === 'USER') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
        <Box
          sx={{
            display: 'inline-flex',
            flexDirection: 'column',
            gap: 1,
            maxWidth: '100%',
            px: 1.75,
            py: 1.25,
            borderRadius: 2,
            bgcolor: 'grey.100',
          }}
        >
          <MarkdownComponent text={message.text} />
          {files.length > 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.75,
                justifyContent: 'flex-end',
              }}
            >
              {files.map((file) => (
                <MessageFileChipComponent
                  key={file.id}
                  id={file.id}
                  name={file.name}
                  mimeType={file.mimeType}
                  isUploading={file.isUploading}
                  downloadFile={downloadFile}
                />
              ))}
            </Box>
          ) : null}
        </Box>
      </Box>
    );
  }

  if (message.role === 'TOOL') {
    return (
      <Box sx={{ width: '100%' }}>
        <Accordion
          disableGutters
          sx={{
            width: '100%',
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            boxShadow: 'none',
            '&::before': { display: 'none' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              '& .MuiAccordionSummary-content': {
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              },
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t('messages.toolCall')}: {toolName}
            </Typography>
            {message.toolApproved === null && onToolApproval ? (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title={t('messages.approveTool')}>
                  <span>
                    <IconButton
                      size="small"
                      color="success"
                      aria-label={t('messages.approveTool')}
                      disabled={isToolApprovalLoading}
                      onClick={(event) => {
                        event.stopPropagation();
                        onToolApproval(true);
                      }}
                    >
                      <CheckIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={t('messages.declineTool')}>
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      aria-label={t('messages.declineTool')}
                      disabled={isToolApprovalLoading}
                      onClick={(event) => {
                        event.stopPropagation();
                        onToolApproval(false);
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            ) : null}
          </AccordionSummary>
          <AccordionDetails sx={{ maxHeight: 1000, overflowY: 'auto' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('messages.arguments')}
                </Typography>
                <ToolArgumentsComponent value={toolArguments} />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('messages.result')}
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    mt: 0.5,
                    p: 1.5,
                    overflow: 'auto',
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                    fontFamily:
                      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                    fontSize: '0.8125rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {formatToolPayload(message.text)}
                </Box>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 1,
        width: '100%',
      }}
    >
      {(thinking || isThinking) && (
        <MessagesListItemThinking
          thinking={thinking}
          isThinking={isThinking}
        />
      )}
      {message.text ? <MarkdownComponent text={message.text} /> : null}
      {files.length > 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.75,
          }}
        >
          {files.map((file) => (
            <MessageFileChipComponent
              key={file.id}
              id={file.id}
              name={file.name}
              mimeType={file.mimeType}
              isUploading={file.isUploading}
              downloadFile={downloadFile}
            />
          ))}
        </Box>
      ) : null}
    </Box>
  );
}
